import React, { useState, useEffect } from 'react';
import { getCurrentUser, deleteCurrentUser, getUserProjectCount, getUserTotalFileSize } from '../../api/client';
import { User } from '../../api/models';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccountDeleted?: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose, onAccountDeleted }) => {
  const [user, setUser] = useState<User | null>(null);
  const [projectCount, setProjectCount] = useState<number>(0);
  const [totalFileSize, setTotalFileSize] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUserProfile();
    }
  }, [isOpen]);

  const fetchUserProfile = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [userData, projectCountData, fileSizeData] = await Promise.all([
        getCurrentUser(),
        getUserProjectCount(),
        getUserTotalFileSize()
      ]);
      setUser(userData);
      setProjectCount(projectCountData.project_count);
      setTotalFileSize(fileSizeData.total_file_size_bytes);
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError('Failed to load user profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      const result = await deleteCurrentUser();
      console.log('Account deletion request:', result);
      
      // Close the modal and trigger logout
      onClose();
      if (onAccountDeleted) {
        onAccountDeleted();
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Chicago'  // Central Time
    });
  };

  if (!isOpen) {
    return null;
  }

  const modalStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1002,
  };

  const contentStyle: React.CSSProperties = {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    maxWidth: '400px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '1px solid #e5e7eb',
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '0',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: '15px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '5px',
  };

  const valueStyle: React.CSSProperties = {
    padding: '8px 12px',
    backgroundColor: '#f9fafb',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    color: '#374151',
  };

  const loadingStyle: React.CSSProperties = {
    textAlign: 'center',
    color: '#6b7280',
    padding: '20px',
  };

  const errorStyle: React.CSSProperties = {
    textAlign: 'center',
    color: '#dc2626',
    padding: '20px',
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={{ margin: 0, color: '#111827' }}>User Profile</h2>
          <button style={closeButtonStyle} onClick={onClose}>
            ×
          </button>
        </div>

        {isLoading && (
          <div style={loadingStyle}>
            Loading user profile...
          </div>
        )}

        {error && (
          <div style={errorStyle}>
            {error}
          </div>
        )}

        {user && !isLoading && !error && (
          <div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Email</label>
              <div style={valueStyle}>{user.email}</div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Name</label>
              <div style={valueStyle}>{user.name}</div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Company</label>
              <div style={valueStyle}>{user.company}</div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Account Created</label>
              <div style={valueStyle}>{formatDate(user.created_at)}</div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Last Activity</label>
              <div style={valueStyle}>{formatDate(user.last_activity)}</div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Number of Projects</label>
              <div style={valueStyle}>{projectCount}</div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Total File Size</label>
              <div style={valueStyle}>{formatFileSize(totalFileSize)}</div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Status</label>
              <div style={valueStyle}>{user.is_active ? 'Active' : 'Inactive'}</div>
            </div>
            
            {/* Delete Account Button */}
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={handleDeleteClick}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deactivating...' : 'Deactivate Account'}
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirmation && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1003,
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              maxWidth: '400px',
              width: '90%',
            }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#dc2626' }}>Deactivate Account</h3>
              <p style={{ margin: '0 0 20px 0', color: '#374151', lineHeight: '1.5' }}>
                Are you sure you want to deactivate your account? Your account will be deactivated 
                and our support team will be notified.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCancelDelete}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deactivating...' : 'Deactivate Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileModal;
